import React, { useCallback, useEffect, useState } from 'react';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import { listMinimums, listOverrides, parseSize, productIdFrom, setMinimum, setOverride, weightReview } from '../../lib/marketAdminSizes';

// Shipping sizes (owner + advisor, 2026-11-02 — PRICING_PLAN §1.4). Three tools:
// the size minimums per class, one product's own size (e.g. a roll-packed
// mattress), and the review list of seller weights under half of Gemini's.
// Every change is audited and reaches every price at once (market_delivered_core).

const T = {
  ar: {
    title: 'أحجام الشحن', sub: 'كل منتج يُشحن على الأكبر من: وزن وحجم البائع، تقدير Gemini، والحد الأدنى لفئته. عدّل الحدود بعد وزن الطلبات الحقيقية.',
    tabs: { minimums: 'الحد الأدنى لكل فئة', overrides: 'حجم منتج محدد', review: 'أوزان مشكوك فيها' },
    cls: 'الفئة', kg: 'كجم', m3: 'م³', staff: 'تسعير بالفريق دائمًا', save: 'حفظ', saved: 'تم الحفظ', bad: 'أرقام غير صحيحة',
    product: 'رابط المنتج أو رقمه', note: 'السبب (مثال: مرتبة مضغوطة ملفوفة)', setSize: 'حفظ حجم المنتج', remove: 'إزالة',
    ovHint: 'هذا الحجم يحل محل حجم البائع وتقدير Gemini والحد الأدنى — استخدمه حين تعرف الحجم الحقيقي.',
    none: 'لا شيء', sellerKg: 'وزن البائع', geminiKg: 'تقدير Gemini', ratio: 'الفرق', variants: 'خيارات', open: 'فتح', fix: 'تحديد الحجم',
    reviewHint: 'منتجات وزن البائع فيها أقل من نصف تقدير Gemini — غالبًا رقم افتراضي. السعر يستخدم الأكبر تلقائيًا؛ هذه القائمة للتصحيح.',
    more: 'المزيد', noProduct: 'لم أجد رقم منتج في النص', noteShort: 'اكتب سببًا (5 أحرف على الأقل)',
  },
  en: {
    title: 'Shipping sizes', sub: 'Every product ships on the largest of: the seller\'s weight and size, Gemini\'s estimate and its class minimum. Adjust the minimums once real orders are weighed.',
    tabs: { minimums: 'Minimum per class', overrides: 'One product\'s size', review: 'Suspect weights' },
    cls: 'Class', kg: 'kg', m3: 'm³', staff: 'Always staff-priced', save: 'Save', saved: 'Saved', bad: 'Invalid numbers',
    product: 'Product link or id', note: 'Reason (e.g. roll-packed mattress)', setSize: 'Save product size', remove: 'Remove',
    ovHint: 'This size replaces the seller\'s, Gemini\'s and the minimum — use it when you know the real size.',
    none: 'Nothing here', sellerKg: 'Seller kg', geminiKg: 'Gemini kg', ratio: 'Ratio', variants: 'Variants', open: 'Open', fix: 'Set size',
    reviewHint: 'Products whose seller weight is under half of Gemini\'s estimate — usually a placeholder. Prices already use the larger; this list is for fixing.',
    more: 'More', noProduct: 'No product id found in the text', noteShort: 'Write a reason (5+ characters)',
  },
};

export default function ConsoleMarketSizes({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const c = T[lang] || T.ar;
  const [tab, setTab] = useState('minimums');
  const [mins, setMins] = useState(null);
  const [draft, setDraft] = useState({});
  const [ovs, setOvs] = useState(null);
  const [ov, setOv] = useState({ product: '', kg: '', m3: '', note: '' });
  const [rev, setRev] = useState(null);
  const [revOffset, setRevOffset] = useState(0);
  const [toast, setToast] = useState('');
  const flash = (m) => { setToast(m); setTimeout(() => setToast(''), 2600); };

  const loadMins = useCallback(async () => {
    const r = await listMinimums();
    if (!r.ok) { flash(r.error); setMins([]); return; }
    setMins(r.data);
    setDraft(Object.fromEntries(r.data.map((m) => [m.code, { kg: m.min_kg ?? '', m3: m.min_m3 ?? '' }])));
  }, []);
  const loadOvs = useCallback(async () => { const r = await listOverrides(); setOvs(r.ok ? r.data : []); if (!r.ok) flash(r.error); }, []);
  const loadRev = useCallback(async () => { const r = await weightReview(revOffset); setRev(r.ok ? r.data : { total: 0, rows: [] }); if (!r.ok) flash(r.error); }, [revOffset]);
  useEffect(() => { if (tab === 'minimums') loadMins(); if (tab === 'overrides') loadOvs(); if (tab === 'review') loadRev(); }, [tab, loadMins, loadOvs, loadRev]);

  const saveMin = async (code) => {
    const kg = parseSize(draft[code]?.kg), m3 = parseSize(draft[code]?.m3);
    if (Number.isNaN(kg) || Number.isNaN(m3)) { flash(c.bad); return; }
    const r = await setMinimum(code, kg, m3);
    flash(r.ok ? c.saved : r.error); if (r.ok) loadMins();
  };
  const saveOv = async () => {
    const id = productIdFrom(ov.product);
    if (!id) { flash(c.noProduct); return; }
    const kg = parseSize(ov.kg), m3 = parseSize(ov.m3);
    if (Number.isNaN(kg) || Number.isNaN(m3)) { flash(c.bad); return; }
    if ((kg != null || m3 != null) && ov.note.trim().length < 5) { flash(c.noteShort); return; }
    const r = await setOverride(id, kg, m3, ov.note.trim());
    flash(r.ok ? c.saved : r.error);
    if (r.ok) { setOv({ product: '', kg: '', m3: '', note: '' }); loadOvs(); }
  };
  const removeOv = async (id) => { const r = await setOverride(id, null, null, null); flash(r.ok ? c.saved : r.error); if (r.ok) loadOvs(); };
  const title = (p) => (p ? (isAr ? p.title_ar || p.title_en : p.title_en || p.title_ar) : '—');

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="market-sizes">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)' }}>
        <div className="ac-page-head"><div><h1 className="ac-h1">{c.title}</h1><p className="ac-sub">{c.sub}</p></div></div>
        <div className="ac-chiprow" style={{ marginBottom: 12 }}>
          {Object.keys(c.tabs).map((k) => (
            <button key={k} type="button" data-sizes-tab={k} className={`ac-chip${tab === k ? ' on' : ''}`} onClick={() => setTab(k)}>{c.tabs[k]}</button>
          ))}
        </div>

        {tab === 'minimums' && (
          <div className="ac-card">
            {!mins ? <p className="ac-sub">…</p> : (
              <table className="ac-table" data-size-minimums={mins.length}>
                <thead><tr><th>{c.cls}</th><th>{c.kg}</th><th>{c.m3}</th><th /></tr></thead>
                <tbody>
                  {mins.map((m) => (
                    <tr key={m.code} data-size-class={m.code}>
                      <td>{isAr ? m.label_ar : m.label_en}</td>
                      {m.staff_quote ? <td colSpan={2}>{c.staff}</td> : <>
                        <td><input className="ac-input" dir="ltr" style={{ width: 80 }} value={draft[m.code]?.kg ?? ''} onChange={(e) => setDraft({ ...draft, [m.code]: { ...draft[m.code], kg: e.target.value } })} /></td>
                        <td><input className="ac-input" dir="ltr" style={{ width: 80 }} value={draft[m.code]?.m3 ?? ''} onChange={(e) => setDraft({ ...draft, [m.code]: { ...draft[m.code], m3: e.target.value } })} /></td>
                      </>}
                      <td>{!m.staff_quote && <button type="button" className="ac-btn ac-btn-sm" onClick={() => saveMin(m.code)}>{c.save}</button>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'overrides' && (
          <div className="ac-card">
            <p className="ac-sub">{c.ovHint}</p>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, margin: '8px 0 16px' }}>
              <input className="ac-input" dir="ltr" style={{ flex: '2 1 260px' }} placeholder={c.product} value={ov.product} onChange={(e) => setOv({ ...ov, product: e.target.value })} />
              <input className="ac-input" dir="ltr" style={{ width: 90 }} placeholder={c.kg} value={ov.kg} onChange={(e) => setOv({ ...ov, kg: e.target.value })} />
              <input className="ac-input" dir="ltr" style={{ width: 90 }} placeholder={c.m3} value={ov.m3} onChange={(e) => setOv({ ...ov, m3: e.target.value })} />
              <input className="ac-input" style={{ flex: '2 1 220px' }} placeholder={c.note} value={ov.note} onChange={(e) => setOv({ ...ov, note: e.target.value })} />
              <button type="button" className="ac-btn" onClick={saveOv}>{c.setSize}</button>
            </div>
            {!ovs ? <p className="ac-sub">…</p> : !ovs.length ? <p className="ac-sub">{c.none}</p> : (
              <table className="ac-table" data-size-overrides={ovs.length}>
                <tbody>
                  {ovs.map((o) => (
                    <tr key={o.product_id}>
                      <td><a href={`/market/${o.product_id}`} target="_blank" rel="noreferrer">{title(o.product)}</a></td>
                      <td dir="ltr">{o.min_kg ?? '—'} {c.kg} · {o.min_m3 ?? '—'} {c.m3}</td>
                      <td>{o.note}</td>
                      <td><button type="button" className="ac-btn ac-btn-sm" onClick={() => removeOv(o.product_id)}>{c.remove}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {tab === 'review' && (
          <div className="ac-card">
            <p className="ac-sub">{c.reviewHint}{rev ? ` (${Number(rev.total).toLocaleString('en-US')})` : ''}</p>
            {!rev ? <p className="ac-sub">…</p> : !rev.rows.length ? <p className="ac-sub">{c.none}</p> : (
              <table className="ac-table" data-weight-review={rev.rows.length}>
                <thead><tr><th /><th>{c.sellerKg}</th><th>{c.geminiKg}</th><th>{c.ratio}</th><th>{c.variants}</th><th /></tr></thead>
                <tbody>
                  {rev.rows.map((r) => (
                    <tr key={r.id}>
                      <td><a href={`/market/${r.id}`} target="_blank" rel="noreferrer">{title(r)}</a></td>
                      <td dir="ltr">{r.seller_kg_min}</td><td dir="ltr">{r.gemini_kg_max}</td>
                      <td dir="ltr">×{Number(r.ratio).toFixed(1)}</td><td>{r.variants}</td>
                      <td><button type="button" className="ac-btn ac-btn-sm" onClick={() => { setOv({ product: r.id, kg: String(r.gemini_kg_max), m3: '', note: '' }); setTab('overrides'); }}>{c.fix}</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {rev && rev.total > revOffset + rev.rows.length && <button type="button" className="ac-btn" onClick={() => setRevOffset(revOffset + 50)}>{c.more}</button>}
          </div>
        )}
        {toast && <div className="ac-toast" role="status">{toast}</div>}
      </div>
    </ConsoleShell>
  );
}
